<template>
  <a-drawer
    :title="$t('avoir.detailDrawerTitle')"
    :width="920"
    :visible="visible"
    destroy-on-close
    @close="$emit('close')"
  >
    <a-spin v-if="loading" class="w-100" />
    <template v-else-if="loadError">
      <a-alert type="error" :message="loadError" show-icon />
    </template>
    <div v-else-if="detail" class="avoir-detail-drawer">
      <!-- Credit note -->
      <h5 class="section-heading">{{ $t("avoir.detailSectionCreditNote") }}</h5>
      <a-descriptions :column="2" bordered size="small" class="mb-3">
        <a-descriptions-item :label="$t('bulletin.label')" :span="2">
          {{ detail.label || "—" }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('comptabilite.reference')">
          {{ detail.reference || "—" }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('recette.montant')">
          {{ formatAmount(detail.amount) }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('action.status')">
          <a-tag :color="statusColor(detail.status)">{{ statusLabel(detail.status) }}</a-tag>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('avoir.detailSupplierLabel')">
          {{ providerDisplayName }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('avoir.detailSchoolYear')">
          {{ schoolYearLabel(detail.schoolarYear) }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('comptabilite.description')" :span="2">
          {{ detail.description || "—" }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('factures.attachments')" :span="2">
          <span v-if="!detail.attachmentKeys || !detail.attachmentKeys.length">{{ $t("factures.noAttachments") }}</span>
          <span v-else>{{ $t("factures.attachmentCount", { n: detail.attachmentKeys.length }) }}</span>
        </a-descriptions-item>
        <a-descriptions-item :label="$t('library.createdAt')">
          {{ formatDateTime(detail.createdAt) }}
        </a-descriptions-item>
        <a-descriptions-item :label="$t('library.createdBy')">
          {{ formatUser(detail.createdBy) }}
        </a-descriptions-item>
        <a-descriptions-item v-if="detail.updatedAt" :label="$t('library.lastUpdate')">
          {{ formatDateTime(detail.updatedAt) }}
        </a-descriptions-item>
      </a-descriptions>

      <!-- Reception -->
      <template v-if="detail.status === 'received'">
        <a-divider />
        <h5 class="section-heading">{{ $t("avoir.detailSectionReception") }}</h5>
        <a-descriptions :column="2" bordered size="small" class="mb-3">
          <a-descriptions-item :label="$t('avoir.receptionValidatedAt')">
            {{ formatDateTime(detail.receivedAt) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('avoir.receptionValidatedBy')">
            {{ formatUser(detail.receivedBy) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.receiptNumber')" :span="2">
            {{ detail.receiptNumber || "—" }}
          </a-descriptions-item>
          <a-descriptions-item v-if="detail.totalReceived != null" :label="$t('avoir.totalReceived')">
            {{ formatAmount(detail.totalReceived) }}
          </a-descriptions-item>
          <a-descriptions-item v-if="detail.methods && detail.methods.length" :label="$t('recette.modePaiement')" :span="2">
            <a-tag v-for="m in detail.methods" :key="m" class="mr-1">{{ $t("decaissement.paymentMethod." + m) }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item
            v-if="detail.caisseAfterTransfer && typeof detail.caisseAfterTransfer === 'object'"
            :label="$t('avoir.transferDestinationCaisse')"
            :span="2"
          >
            {{ caisseName(detail.caisseAfterTransfer) }}
          </a-descriptions-item>
        </a-descriptions>

        <div v-if="splitTableRows.length" class="mb-2">
          <span class="font-weight-bold">{{ $t("avoir.receptionSplitLines") }}</span>
        </div>
        <a-table
          v-if="splitTableRows.length"
          size="small"
          row-key="_idx"
          :pagination="false"
          :columns="splitColumns"
          :data-source="splitTableRows"
        />
      </template>

      <template v-else-if="detail.status === 'pending_reception'">
        <a-divider />
        <a-alert type="info" show-icon :message="$t('avoir.detailPendingReception')" />
      </template>

      <!-- Original invoice -->
      <a-divider />
      <h5 class="section-heading">{{ $t("avoir.detailSectionOriginalInvoice") }}</h5>
      <template v-if="depense">
        <a-descriptions :column="2" bordered size="small" class="mb-3">
          <a-descriptions-item :label="$t('decaissement.invoiceNumber')">
            {{ depense.invoiceNumber || "—" }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('factures.reference')">
            {{ depense.reference || "—" }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('bulletin.label')" :span="2">
            {{ depense.label || "—" }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.invoiceDate')">
            {{ formatDate(depense.date) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.depenseCreatedAt')">
            {{ formatDate(depense.entryDate || depense.createdAt) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('fournisseur.invoiceStatus')">
            <a-tag :color="invoiceStatusColor(depense.invoiceStatus)">{{ invoiceStatusLabel(depense.invoiceStatus) }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('fournisseur.totalAmount')">
            {{ formatAmount(depense.amountTTC != null ? depense.amountTTC : depense.amount || depense.money) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('fournisseur.totalPaid')">
            {{ formatAmount(depense.totalPaid) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.description')" :span="2">
            {{ depense.description || "—" }}
          </a-descriptions-item>
        </a-descriptions>
        <div v-if="supplierProfileId" class="mt-2">
          <router-link :to="'/decaissement/fournisseur/' + supplierProfileId">
            <a-button type="primary" size="small" icon="user">{{ $t("factures.viewSupplier") }}</a-button>
          </router-link>
        </div>
      </template>
      <a-alert v-else type="warning" show-icon :message="$t('avoir.depenseMissingForDetail')" />
    </div>
  </a-drawer>
</template>

<script>
import moment from "moment";
import { fetchCreditNoteById } from "@/services/api/compV3/paymentEntry";
import { showApiError } from "@/utilities/showApiError";

export default {
  name: "AvoirDetailDrawer",
  props: {
    visible: { type: Boolean, default: false },
    /** DepenseAvoir id */
    avoirId: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      loadError: null,
      detail: null,
    };
  },
  computed: {
    depense() {
      const d = this.detail && this.detail.depense;
      return d && typeof d === "object" ? d : null;
    },
    providerDisplayName() {
      const p = this.detail && this.detail.provider;
      if (p && typeof p === "object") {
        return p.fullName || p.company || "—";
      }
      return "—";
    },
    supplierProfileId() {
      const p = this.detail && this.detail.provider;
      if (p && typeof p === "object" && p._id) return String(p._id);
      if (typeof p === "string") return p;
      return null;
    },
    splitTableRows() {
      const s = (this.detail && this.detail.splitPayments) || [];
      return s.map((sp, i) => ({ ...sp, _idx: "s" + i }));
    },
    splitColumns() {
      const vm = this;
      return [
        {
          title: this.$t("encaissementValidation.paymentMethod"),
          dataIndex: "method",
          key: "method",
          customRender(text) {
            return vm.$t("decaissement.paymentMethod." + text);
          },
        },
        {
          title: this.$t("recette.montant"),
          dataIndex: "amount",
          key: "amount",
          customRender(text) {
            return text != null ? Number(text).toFixed(3) : "—";
          },
        },
        {
          title: this.$t("avoir.splitDestination"),
          key: "dest",
          customRender(_, row) {
            return vm.splitDestinationLabel(row);
          },
        },
      ];
    },
  },
  watch: {
    visible(v) {
      if (v && this.avoirId) {
        this.fetchDetail();
      }
      if (!v) {
        this.detail = null;
        this.loadError = null;
      }
    },
    avoirId(id) {
      if (this.visible && id) {
        this.fetchDetail();
      }
    },
  },
  methods: {
    formatAmount(v) {
      if (v == null || Number.isNaN(Number(v))) return "—";
      return Number(v).toFixed(3);
    },
    formatDate(val) {
      return val ? moment(val).format("DD/MM/YYYY") : "—";
    },
    formatDateTime(val) {
      return val ? moment(val).format("DD/MM/YYYY HH:mm") : "—";
    },
    formatUser(u) {
      if (u == null || u === "" || u === "--") return "—";
      return String(u);
    },
    schoolYearLabel(sy) {
      if (!sy || typeof sy !== "object") return "—";
      if (sy.start && sy.end) {
        return `${moment(sy.start).format("YYYY")}-${moment(sy.end).format("YYYY")}`;
      }
      return "—";
    },
    caisseName(c) {
      if (!c || typeof c !== "object") return "—";
      const t = c.type === "BANK" ? this.$t("caisse.BANK") : this.$t("caisse.caisse");
      return `${c.name || c.reference || "—"} (${t})`;
    },
    splitDestinationLabel(sp) {
      if (sp._caisseBank && typeof sp._caisseBank === "object") {
        return `${sp._caisseBank.name || sp._caisseBank.reference || "—"} (${this.$t("caisse.BANK")})`;
      }
      if (sp._caisse && typeof sp._caisse === "object") {
        return sp._caisse.name || sp._caisse.reference || "—";
      }
      return "—";
    },
    statusColor(s) {
      if (s === "received") return "green";
      if (s === "cancelled") return "default";
      return "orange";
    },
    statusLabel(s) {
      if (s === "received") return this.$t("avoir.received");
      if (s === "cancelled") return this.$t("avoir.cancelled");
      return this.$t("avoir.pendingReception");
    },
    invoiceStatusColor(status) {
      if (status === "paid") return "green";
      if (status === "partial") return "orange";
      return "red";
    },
    invoiceStatusLabel(status) {
      if (status === "paid") return this.$t("fournisseur.paid");
      if (status === "partial") return this.$t("fournisseur.partial");
      return this.$t("fournisseur.unpaid");
    },
    async fetchDetail() {
      const id = this.avoirId;
      if (!id) return;
      this.loading = true;
      this.loadError = null;
      this.detail = null;
      try {
        const res = await fetchCreditNoteById(id);
        this.detail = res.data || null;
        if (!this.detail) {
          this.loadError = this.$t("warning.connectionProblem");
        }
      } catch (e) {
        showApiError(this, e);
        this.loadError = this.$t("warning.connectionProblem");
        this.detail = null;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.section-heading {
  margin-bottom: 12px;
  font-weight: 600;
}
.avoir-detail-drawer {
  padding-bottom: 24px;
}
</style>
